import { lazy, Suspense, ComponentType, ReactNode } from 'react';

/**
 * Creates a lazily-loaded React component with Suspense wrapper.
 *
 * @example
 * const LazyModal = createLazyComponent(
 *    () => import('components/Modal'),
 *    <LoadingSkeleton />
 * );
 */
export function createLazyComponent<T extends object>(
   importFn: () => Promise<{ default: ComponentType<T> }>,
   fallback?: ReactNode
) {
   const LazyComponent = lazy(importFn);
   return function LazyWrapper(props: T) {
      return (
         <Suspense fallback={fallback ?? null}>
            <LazyComponent {...props} />
         </Suspense>
      );
   };
}

/**
 * Preloads a component without rendering it.
 * Useful for loading components on hover or other predictive events.
 *
 * @example
 * onMouseEnter={() => preloadComponent(() => import('components/Modal'))}
 */
export function preloadComponent(importFn: () => Promise<unknown>): void {
   void importFn();
}
